'use strict';

const libPictView = require('pict-view');

// Full-page documentation browser ("Headlight Academy"). A reading-first companion to the compact inline help
// panel: it reuses the SAME doc pipeline (pict-section-inlinedocumentation → AcademyContent-layered markdown), so
// the TOC and every article match the panel + the doc editor, with customer overrides applied. The view renders a
// static shell; the Academy provider paints the TOC + article into it (see Academy-Provider.js).

const HELP_CENTER_CSS = /*css*/`
#Academy-Container.ha-wrap { display:flex; flex-direction:column; min-height: calc(100vh - 110px); background: var(--theme-color-surface, #fff); border-radius: 10px; box-shadow: 0 1px 3px rgba(15,23,42,.08); overflow:hidden; }
.ha-header { display:flex; justify-content:space-between; align-items:center; gap:16px; flex-wrap:wrap; padding: 20px 26px; border-bottom:1px solid var(--theme-color-border, #e2e8f0); }
.ha-header-main { display:flex; align-items:center; gap:14px; min-width:0; }
.ha-header-icon { font-size:24px; color: var(--theme-color-brand-primary, #5650e6); }
.ha-title { margin:0; font-size:21px; font-weight:700; color: var(--theme-color-text, #0f172a); letter-spacing:-.01em; }
.ha-subtitle { margin:2px 0 0; font-size:13px; color: var(--theme-color-text-muted, #64748b); }
.ha-search { position:relative; flex:0 0 auto; }
.ha-search input { width:240px; max-width:52vw; padding:8px 12px 8px 32px; border:1px solid var(--theme-color-border, #e2e8f0); border-radius:8px; font-size:13px; background: var(--theme-color-surface-muted, #f8fafc); color: var(--theme-color-text, #0f172a); }
.ha-search input:focus { outline:none; border-color: var(--theme-color-brand-primary, #5650e6); box-shadow:0 0 0 3px rgba(86,80,230,.12); background:#fff; }
.ha-search i { position:absolute; left:11px; top:50%; transform:translateY(-50%); color:#94a3b8; font-size:12px; }
.ha-body { display:flex; align-items:flex-start; flex:1; min-height:0; }
.ha-toc { flex:0 0 272px; width:272px; align-self:stretch; position:sticky; top:0; max-height: calc(100vh - 110px); overflow-y:auto; padding:16px 10px 24px; border-right:1px solid var(--theme-color-border, #e2e8f0); background: var(--theme-color-surface-muted, #f8fafc); }
.ha-toc-group { margin-bottom:14px; }
.ha-toc-group-title { font-size:11px; text-transform:uppercase; letter-spacing:.06em; color:#94a3b8; font-weight:700; padding:4px 10px 6px; }
.ha-toc-item { display:block; padding:6px 10px; border-radius:6px; color: var(--theme-color-text, #334155); text-decoration:none; font-size:13.5px; line-height:1.35; cursor:pointer; }
.ha-toc-item:hover { background: rgba(86,80,230,.08); color: var(--theme-color-brand-primary, #5650e6); }
.ha-toc-item.ha-active { background: var(--theme-color-brand-primary, #5650e6); color:#fff; font-weight:600; }
.ha-toc-empty { padding:12px; color:#94a3b8; font-size:13px; font-style:italic; }
.ha-article { flex:1; min-width:0; overflow-y:auto; padding: 26px 40px 60px; }
.ha-loading, .ha-empty { color:#64748b; font-size:14px; padding:24px 0; display:flex; align-items:center; gap:10px; }
.ha-empty { flex-direction:column; align-items:flex-start; gap:6px; }
.ha-empty i { color:#cf5b5b; font-size:20px; }
.ha-spinner { width:16px; height:16px; border:2px solid #cbd5e1; border-top-color: var(--theme-color-brand-primary, #5650e6); border-radius:50%; display:inline-block; animation: ha-spin .7s linear infinite; }
@keyframes ha-spin { to { transform: rotate(360deg); } }

/* Article typography (rendered markdown) */
.ha-doc { max-width: 780px; color: var(--theme-color-text, #1e293b); font-size:15px; line-height:1.65; }
.ha-doc > :first-child { margin-top:0; }
.ha-doc h1 { font-size:26px; font-weight:700; letter-spacing:-.02em; margin:0 0 8px; color: var(--theme-color-text, #0f172a); }
.ha-doc h2 { font-size:19px; font-weight:700; margin:28px 0 8px; padding-bottom:5px; border-bottom:1px solid var(--theme-color-border, #eef2f6); color: var(--theme-color-text, #0f172a); }
.ha-doc h3 { font-size:16px; font-weight:700; margin:22px 0 6px; }
.ha-doc p { margin:0 0 12px; }
.ha-doc ul, .ha-doc ol { margin:0 0 12px; padding-left:22px; }
.ha-doc li { margin:4px 0; }
.ha-doc a { color: var(--theme-color-brand-primary, #5650e6); text-decoration:none; }
.ha-doc a:hover { text-decoration:underline; }
.ha-doc code { background: var(--theme-color-surface-muted, #f1f5f9); padding:1.5px 5px; border-radius:4px; font-size:.88em; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }
.ha-doc pre { background: #0f172a; color:#e2e8f0; padding:14px 16px; border-radius:8px; overflow-x:auto; margin:0 0 14px; }
.ha-doc pre code { background:none; padding:0; color:inherit; font-size:13px; }
.ha-doc blockquote { margin:0 0 14px; padding:6px 14px; border-left:3px solid var(--theme-color-brand-primary, #5650e6); background: rgba(86,80,230,.05); color:#475569; }
.ha-doc table { border-collapse:collapse; width:100%; margin:0 0 16px; font-size:14px; }
.ha-doc th, .ha-doc td { border:1px solid var(--theme-color-border, #e2e8f0); padding:7px 10px; text-align:left; }
.ha-doc th { background: var(--theme-color-surface-muted, #f8fafc); font-weight:600; }
.ha-doc img { max-width:100%; border-radius:6px; }
.ha-doc hr { border:none; border-top:1px solid var(--theme-color-border, #e2e8f0); margin:22px 0; }
@media (max-width: 820px) {
	.ha-body { flex-direction:column; }
	.ha-toc { position:static; width:auto; flex-basis:auto; max-height:none; border-right:none; border-bottom:1px solid var(--theme-color-border, #e2e8f0); }
	.ha-article { padding:20px; }
}
`;

const HELP_CENTER_TEMPLATES =
[
	{
		Hash: 'Academy-Shell',
		Template: /*html*/`
			<div id="Academy-Container" class="ha-wrap">
				<header class="ha-header">
					<div class="ha-header-main">
						<i class="fas fa-graduation-cap ha-header-icon"></i>
						<div>
							<h1 id="Academy-Title" class="ha-title">Headlight Academy</h1>
							<p id="Academy-Subtitle" class="ha-subtitle">Guides and reference for configuring your platform.</p>
						</div>
					</div>
					<div class="ha-search">
						<i class="fas fa-magnifying-glass"></i>
						<input id="Academy-Filter" type="text" placeholder="Filter articles…" autocomplete="off"
							oninput="_Pict.providers.Academy.onFilterInput(this.value)" />
					</div>
				</header>
				<div class="ha-body">
					<nav id="Academy-TOC" class="ha-toc"></nav>
					<main id="Academy-Article" class="ha-article"></main>
				</div>
			</div>`,
	},
];

class AcademyView extends libPictView
{
	onAfterRender()
	{
		const tmpProvider = this.pict.providers.Academy;
		if (tmpProvider && typeof tmpProvider.onViewRendered === 'function') { tmpProvider.onViewRendered(); }
		return super.onAfterRender();
	}
}

AcademyView.default_configuration =
{
	ViewIdentifier: 'Academy',
	DefaultRenderable: 'Academy-Shell',
	DefaultDestinationAddress: '#PlatformConfiguration-Application-Container',
	AutoRender: false,
	CSS: HELP_CENTER_CSS,
	Templates: HELP_CENTER_TEMPLATES,
	Renderables: [ { RenderableHash: 'Academy-Shell', TemplateHash: 'Academy-Shell', DestinationAddress: '#PlatformConfiguration-Application-Container', RenderMethod: 'replace' } ],
};

module.exports = AcademyView;
